/**
 * API Utilities
 * Wrapper around fetch with authentication and error handling
 */

import { API_BASE_URL, API_ENDPOINTS, STORAGE_KEYS } from '../config/api.js';

/**
 * Get auth token from local storage
 */
export function getAuthToken() {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
}

/**
 * Set auth token in local storage
 */
export function setAuthToken(token) {
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
}

/**
 * Remove auth token from local storage
 */
export function removeAuthToken() {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER_DATA);
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return !!getAuthToken();
}

/**
 * Make an authenticated API request
 */
export async function apiRequest(endpoint, options = {}) {
  const token = getAuthToken();
  
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };
  
  // Add auth token if available
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Add body if provided
  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }
  
  const url = `${API_BASE_URL}${endpoint}`;
  
  try {
    const response = await fetch(url, config);
    
    // Parse JSON response
    const data = await response.json();
    
    // Handle errors
    if (!response.ok) {
      const error = new Error(data.error || data.message || 'Request failed');
      error.status = response.status;
      error.data = data;
      throw error;
    }
    
    return data;
  } catch (error) {
    // If token is invalid, clear it
    if (error.status === 401) {
      removeAuthToken();
    }
    throw error;
  }
}

/**
 * Convenience methods
 */
export const api = {
  get: (endpoint, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return apiRequest(url, { method: 'GET' });
  },
  
  post: (endpoint, body) => {
    return apiRequest(endpoint, {
      method: 'POST',
      body
    });
  },
  
  put: (endpoint, body) => {
    return apiRequest(endpoint, {
      method: 'PUT',
      body
    });
  },
  
  delete: (endpoint) => {
    return apiRequest(endpoint, {
      method: 'DELETE'
    });
  }
};

/**
 * Favorites API methods
 */
export const favoritesApi = {
  getFavorites: () => api.get(API_ENDPOINTS.GET_FAVORITES),
  
  addFavorite: (comboCode, comboName) => 
    api.post(API_ENDPOINTS.ADD_FAVORITE, { comboCode, comboName }),
  
  removeFavorite: (comboCode) => 
    api.delete(API_ENDPOINTS.REMOVE_FAVORITE(comboCode))
};

